import {addUrlToHistoryStack} from '@github-ui/history'
import {ssrSafeLocation} from '@github-ui/ssr-utils'

/** Enough to identify the issue; callers add whatever else their own identifier type carries. */
export type SidePanelIssue = {owner: string; repo: string; number: number}

/**
 * The side panel's URL contract: `?issue=owner|repo|number`. Shared by the Relay issue page and the
 * Data Router route so a link resolves to the same issue on either stack.
 */
export const SIDE_PANEL_SEARCH_PARAM = 'issue'

// Dot-only segments are path traversal once interpolated: `/o/../issues/1` is `/issues/1`.
const NAME_SEGMENT = /^(?!\.+$)[\w.-]+$/

export function parseSidePanelIssue(href: string = ssrSafeLocation.href): SidePanelIssue | null {
  const reference = new URL(href, ssrSafeLocation.origin).searchParams.get(SIDE_PANEL_SEARCH_PARAM)
  if (!reference) return null

  const parts = reference.split('|')
  if (parts.length !== 3) return null

  const [owner, repo, rawNumber] = parts
  // Interpolated into hrefs, so `//evil.com` as an owner would resolve off-site.
  if (!owner || !repo || !NAME_SEGMENT.test(owner) || !NAME_SEGMENT.test(repo)) return null

  const number = Number(rawNumber)
  // Rejects `0`, negatives and `12abc`, all of which `parseInt` would have accepted in some form.
  if (!Number.isSafeInteger(number) || number < 1) return null

  return {owner, repo, number}
}

/** `pushState`, not a router navigation: the panel is not a route, and navigating would refetch. */
export function writeSidePanelIssueToUrl(issue: SidePanelIssue | null): void {
  const url = new URL(ssrSafeLocation.href, ssrSafeLocation.origin)
  if (issue) {
    url.searchParams.set(SIDE_PANEL_SEARCH_PARAM, `${issue.owner}|${issue.repo}|${issue.number}`)
  } else {
    url.searchParams.delete(SIDE_PANEL_SEARCH_PARAM)
  }
  addUrlToHistoryStack(url.toString())
}
